import { ApolloType } from "@/enum/apollo.enum";
import { ListSourceType } from "@/enum/list.enum";
import { ScrapeLevel } from "@/enum/job.enum";
import { List } from "@/pages/Private/pages/List/schema/list";
import { UpdateApolloSavePeopleRequestParams } from "@/types/apollo";
import {
	CompanyEnrich,
	CreateCompanyEnrich,
} from "@/pages/Private/pages/CompanyEnrich/schema/companyEnrich";
import { CreateFundingMa, FundingMa } from "@/pages/Private/pages/FundingMa/schema/funding-ma";
import { CreateJob, Job } from "@/pages/Private/pages/Job/schema/job";
import { LiPost } from "@/pages/Private/pages/LiPost/schema/liPost";
import { CreateMap, Map as MapSource } from "@/pages/Private/pages/Map/schema/map";
import { CreateParallelAi, ParallelAi } from "@/pages/Private/pages/ParallelAi/schema/parallelAi";

import {
	buildAiCompanyPayload,
	buildAiNewsPayload,
	buildFundingPayload,
	buildJobPayload,
	buildLeadDbPayload,
	buildLookalikePayload,
	buildMapPayload,
	buildNewInRolePayload,
	buildPeopleAtCompanyConfig,
	buildTopicPayload,
} from "./onboardingListPayloads";
import {
	isCompanySourced,
	ONBOARDING_LIST_TYPES,
	OnboardingListConfig,
	OnboardingListTypeKey,
} from "./onboardingListTypes";

interface Unwrappable<T> {
	unwrap: () => Promise<T>;
}

/** The RTK mutation triggers the creation chain needs — passed in from the page's hooks. */
export interface OnboardingListMutations {
	createList: (body: Partial<List>) => Unwrappable<List>;
	createJob: (body: CreateJob) => Unwrappable<Partial<Job>>;
	createCompanyEnrich: (body: CreateCompanyEnrich) => Unwrappable<CompanyEnrich>;
	createLiPost: (body: Partial<LiPost>) => Unwrappable<Partial<LiPost>>;
	createMap: (body: CreateMap) => Unwrappable<Partial<MapSource>>;
	createParallelAi: (body: CreateParallelAi) => Unwrappable<Partial<ParallelAi>>;
	createFundingMa: (body: CreateFundingMa) => Unwrappable<FundingMa>;
	/** Reads back the created list — the apollo the search profile is saved on. */
	getList: (id: number) => Unwrappable<List>;
	updateApollo: (body: UpdateApolloSavePeopleRequestParams) => Unwrappable<unknown>;
}

/**
 * Leads an onboarding list harvests on its first run (PREC-3861). The lists are
 * created on the demo credits and exist to show what a list produces, so they
 * stay far below the app-wide default of 100 — the user raises the cap in the
 * list's settings once they want the full population.
 */
export const ONBOARDING_LIST_MAX_PROSPECTS = 25;

/** The sources whose list container carries the enrichment scrape level (see `Tile.tsx`). */
const SCRAPE_LEVEL_SOURCES: ListSourceType[] = [
	ListSourceType.JOBS,
	ListSourceType.MAPS,
	ListSourceType.PARALLEL_AI,
];

const sourceTypeFor = (key: OnboardingListTypeKey): ListSourceType => {
	const definition = ONBOARDING_LIST_TYPES.find((entry) => entry.key === key);

	if (!definition) {
		throw new Error(`unknown onboarding list type: ${key}`);
	}

	return definition.sourceType;
};

/** Creates the list container the source config is then attached to. */
const createContainer = async (
	config: OnboardingListConfig,
	listName: string,
	mutations: OnboardingListMutations
): Promise<List> => {
	const sourceType = sourceTypeFor(config.kind);

	return mutations
		.createList({
			sourceType,
			scrapeLevel: SCRAPE_LEVEL_SOURCES.includes(sourceType)
				? ScrapeLevel.GET_ENRICHMENT
				: undefined,
			isNewCreationProcess: true,
			name: listName,
			maxProspects: ONBOARDING_LIST_MAX_PROSPECTS,
		})
		.unwrap();
};

/** Attaches the type's source config to an already-created list container. */
const createSource = async (
	config: OnboardingListConfig,
	listId: number,
	listName: string,
	mutations: OnboardingListMutations
): Promise<void> => {
	const context = { listId, listName };

	switch (config.kind) {
		case "hiring":
			await mutations.createJob(buildJobPayload(config, context)).unwrap();

			return;
		case "lookalike":
			await mutations.createCompanyEnrich(buildLookalikePayload(config, context)).unwrap();

			return;
		case "leadDb":
			await mutations.createCompanyEnrich(buildLeadDbPayload(config, context)).unwrap();

			return;
		case "newInRole":
			await mutations.createCompanyEnrich(buildNewInRolePayload(config, context)).unwrap();

			return;
		case "topic":
			await mutations.createLiPost(buildTopicPayload(config, context)).unwrap();

			return;
		case "gmaps":
			await mutations.createMap(buildMapPayload(config, context)).unwrap();

			return;
		case "aiCompany":
			await mutations.createParallelAi(buildAiCompanyPayload(config, context)).unwrap();

			return;
		case "aiNews":
			await mutations.createParallelAi(buildAiNewsPayload(config, context)).unwrap();

			return;
		case "funding":
		case "mna":
			await mutations.createFundingMa(buildFundingPayload(config, context)).unwrap();
	}
};

/**
 * Saves the "who to find at each qualifying company" profile on the list's
 * SOURCE apollo — the same record the in-app details step writes.
 *
 * The apollo is created together with the list container, but the create
 * response doesn't carry it, so the list is read back for its id.
 */
const savePeopleAtCompany = async (
	config: OnboardingListConfig,
	listId: number,
	listName: string,
	mutations: OnboardingListMutations
): Promise<void> => {
	if (!isCompanySourced(config)) {
		return;
	}

	const list = await mutations.getList(listId).unwrap();
	const apollo = list.apollos?.find((entry) => entry.type === ApolloType.SOURCE);

	if (!apollo) {
		throw new Error(`list ${listId} has no source apollo to save the search profile on`);
	}

	await mutations
		.updateApollo({
			id: apollo.id,
			name: listName,
			type: ApolloType.SOURCE,
			listId,
			updateType: "entityFilter",
			config: buildPeopleAtCompanyConfig(config.peopleAtCompany),
		})
		.unwrap();
};

/**
 * Creates a real list (container + source config) from an onboarding picker
 * config — the same two calls the in-app create flow chains, with sensible
 * defaults for everything the compact onboarding forms don't ask about.
 *
 * Returns the created list's id so the step can remember what it made and not
 * create it again on a second visit (see `syncOnboardingLists`).
 */
export async function createOnboardingList(
	config: OnboardingListConfig,
	listName: string,
	mutations: OnboardingListMutations
): Promise<number> {
	const list = await createContainer(config, listName, mutations);

	await createSource(config, list.id, listName, mutations);
	await savePeopleAtCompany(config, list.id, listName, mutations);

	return list.id;
}
